import React from "react";
import { Helmet } from "react-helmet-async";
import { buildBreadcrumbJsonLd } from "@/lib/seo-schemas";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Tv, Zap, Shield, Clock, TrendingUp } from "lucide-react";

const AbonnementIptv6Mois = () => {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Abonnement IPTV 6 Mois - Roi du Streaming",
    "description": "Abonnement IPTV premium 6 mois avec le meilleur rapport qualité/prix. Code Xtream livré automatiquement. +15000 chaînes HD/4K.",
    "image": ["https://roidustreaming.fr/favicon.png"],
    "brand": { "@type": "Brand", "name": "Roi du Streaming" },
    "offers": {
      "@type": "Offer",
      "name": "Abonnement IPTV 6 Mois",
      "price": "34.99",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": "https://roidustreaming.fr/abonnement-iptv-6-mois",
      "priceValidUntil": "2026-12-31",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "FR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" }
        }
      }
    },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "892", "bestRating": "5", "worstRating": "1" }
  };

  const breadcrumbSchema = buildBreadcrumbJsonLd([
    { name: "Accueil", href: "/" },
    { name: "Nos Offres", href: "/nos-offres" },
    { name: "Abonnement IPTV 6 Mois", href: "/abonnement-iptv-6-mois" },
  ]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Abonnement IPTV 6 Mois – Meilleur rapport qualité/prix | Roi du Streaming</title>
        <meta name="description" content="Abonnement IPTV 6 mois au meilleur prix. Code Xtream livré instantanément. +15000 chaînes HD/4K, VOD, sports. Compatible Smart TV, Firestick, Android." />
        <meta name="keywords" content="abonnement iptv 6 mois, iptv 6 mois pas cher, meilleur iptv 6 mois, code xtream 6 mois, iptv premium" />
        <link rel="canonical" href="https://roidustreaming.fr/abonnement-iptv-6-mois" />
        <meta property="og:title" content="Abonnement IPTV 6 Mois – Meilleur rapport qualité/prix" />
        <meta property="og:description" content="Abonnement IPTV 6 mois au meilleur prix. Code Xtream livré instantanément." />
        <meta property="og:url" content="https://roidustreaming.fr/abonnement-iptv-6-mois" />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      <main className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="text-sm text-muted-foreground mb-8" aria-label="Fil d'Ariane">
            <ol className="flex flex-wrap gap-2">
              <li><Link to="/" className="hover:text-primary transition-colors">Accueil</Link></li>
              <li>/</li>
              <li><Link to="/nos-offres" className="hover:text-primary transition-colors">Nos Offres</Link></li>
              <li>/</li>
              <li className="text-foreground">Abonnement 6 Mois</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-green-500/10 text-green-400 text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Meilleur rapport durée/prix
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Abonnement IPTV 6 Mois – <span className="gradient-text">Le choix malin</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              L'abonnement 6 mois offre le meilleur équilibre entre durée et prix. Profitez de tous vos contenus 
              préférés sans vous soucier du renouvellement pendant 6 mois.
            </p>
          </div>

          {/* Price Card */}
          <div className="card-premium p-8 rounded-2xl mb-12 text-center border-2 border-green-500/30">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-green-500 text-white text-sm font-medium rounded-full">
              Populaire
            </div>
            <div className="text-5xl md:text-6xl font-bold text-primary mb-2">34,99€</div>
            <p className="text-muted-foreground mb-2">pour 6 mois d'accès complet</p>
            <p className="text-sm text-green-400 mb-6">Soit seulement 5,83€/mois</p>
            <Link to="/nos-offres">
              <Button className="btn-primary text-white font-semibold px-8 py-4 text-lg">
                Souscrire maintenant
              </Button>
            </Link>
          </div>

          {/* Content Section */}
          <div className="prose prose-lg prose-invert max-w-none mb-12">
            <p>
              L'abonnement 6 mois représente le choix idéal pour ceux qui veulent profiter sereinement de leur 
              service IPTV. Avec le meilleur rapport durée/prix, vous bénéficiez de 6 mois complets d'accès 
              à l'ensemble de notre catalogue.
            </p>
            <p>
              Votre code Xtream est généré et envoyé automatiquement après validation de votre commande. 
              Aucune action supplémentaire n'est requise : vous pouvez commencer à regarder immédiatement.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Tv className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">+15 000 chaînes</h3>
                <p className="text-sm text-muted-foreground">Chaînes internationales en direct</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Qualité HD & 4K</h3>
                <p className="text-sm text-muted-foreground">Streaming optimal sur tous appareils</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">VOD complète</h3>
                <p className="text-sm text-muted-foreground">Films, séries, documentaires</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Sports en direct</h3>
                <p className="text-sm text-muted-foreground">Matchs et événements sportifs</p>
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="card-premium p-8 rounded-2xl mb-12">
            <h2 className="text-2xl font-bold mb-6">Pourquoi choisir 6 mois ?</h2>
            <ul className="space-y-4">
              {[
                "Meilleur rapport qualité/prix de notre catalogue",
                "Seulement 5,83€ par mois au lieu de 8,33€",
                "6 mois de tranquillité sans renouvellement",
                "Accès complet à toutes les chaînes et la VOD",
                "Livraison automatique immédiate par email",
                "Support client inclus via le site"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <p className="text-muted-foreground mb-6">
              Une question ? Notre support est disponible directement sur le site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nos-offres">
                <Button className="btn-primary text-white font-semibold px-8">
                  Voir toutes les offres
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" className="px-8">
                  Questions fréquentes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AbonnementIptv6Mois;
